import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class IncidentTrackerService {
  constructor(private readonly http: HttpClient) {}

  getIncidentTrackerList(offSet?: any, end?: any, moduleName: string = "") {
    const params = `end=${end}&offset=${offSet}`;
    return this.http.get(`grc/incident?${params}&module=${moduleName}`);
  }

  getProgram(moduleName: any = "") {
    return this.http.get(`org/programs/?module=${moduleName}`);
  }

  getProject(moduleName: any = "") {
    return this.http.get(`projects/?module=${moduleName}`);
  }

  getVendor() {
    return this.http.get(`vendors/names/`);
  }

  getDomain() {
    return this.http.get(`grc/incidentdomain/`);
  }

  getSubDomain() {
    return this.http.get(`grc/incidentsubdomain/`);
  }

  getUser() {
    return this.http.get(`users/user-list/`);
  }

  addNewIncidentTracker(data: any) {
    return this.http.post("grc/incident/", data);
  }

  updateIncidentTracker(id: any, data: any, moduleName: string = "") {
    return this.http.put(`grc/incident/${id}/?module=${moduleName}`, data);
  }

  deleteIncidentTracker(incidentId: any, moduleName: string = "") {
    return this.http.delete(`grc/incident/${incidentId}/?module=${moduleName}`);
  }

  getOwner() {
    return this.http.get(`users/user-list/`);
  }

  getIncidentType() {
    return this.http.get("base_value/?type=grc_incident_type");
  }

  getAuditResponseChoices() {
    return this.http.get("base_value/?type=audit_template_response");
  }

  downloadTemplate() {
    return this.http.get(`grc/audit-templates/default/`, {
      responseType: "blob",
    });
  }

  getAuditTemplates() {
    return this.http.get(`grc/audit-templates/`);
  }

  deletedAuditTemplate(id: any) {
    return this.http.delete(`grc/audit-templates/${id}`);
  }

  uploadFile(payload: any) {
    return this.http.post(`grc/audit-templates/`, payload);
  }

  createManualTemplate(payload: any) {
    return this.http.post(`grc/audit-templates/create-template/`, payload);
  }

  getAuditTemplateCategory() {
    return this.http.get("base_value/?type=grc_audit_category");
  }

  getFrequencies() {
    return this.http.get("base_value/?type=grc_frequency_type");
  }

  getAuditTypes() {
    return this.http.get("base_value/?type=grc_audit_type");
  }

  getFilteredAuditTemplates(qParam: string) {
    return this.http.get(`grc/audit-templates/?${qParam}`);
  }

  getFilterOwner(params: any) {
    return this.http.get("users/filter-user/", { params });
  }

  createAuditInstance(payload: any, moduleName: string = "") {
    return this.http.post(`grc/audit-instances/?module=${moduleName}`, payload);
  }

  updateAuditInstance(
    instanceId: number,
    payload: any,
    moduleName: string = ""
  ) {
    return this.http.patch(
      `grc/audit-instances/${instanceId}/?module=${moduleName}`,
      payload
    );
  }

  getAuditInstances() {
    return this.http.get("grc/audit-instances/");
  }

  getAuditInstancesByProjectId(projectId: any) {
    return this.http.get(`grc/audit-instances/?project=${projectId}`);
  }

  getRecommendedAuditTemplatesByProjectId(projectId: any) {
    return this.http.get(
      `grc/audit-templates/recommended-templates/?project=${projectId}`
    );
  }

  getInstanceInfo(instanceId: any) {
    return this.http.get(`grc/audit-instances/${instanceId}/`);
  }

  getSeverityReport(instanceId: any) {
    return this.http.get(
      `grc/audit-instances/severity-report?id=${instanceId}`
    );
  }

  downloadResponseTemplate(templateId: any) {
    return this.http.get(
      `grc/audit-templates/${templateId}/response-template`,
      { responseType: "blob" }
    );
  }

  getIncidentStatus() {
    return this.http.get("base_value/?type=grc_incident_status");
  }

  getIncidentSeverity() {
    return this.http.get("base_value/?type=grc_incident_severity");
  }

  getIncidentImpact() {
    return this.http.get("base_value/?type=grc_incident_impact");
  }

  getComponents() {
    return this.http.get("master/infrastructures/names/");
  }

  editInstanceResponse(responseId: any, payload: any) {
    return this.http.patch(`grc/audit-responses/${responseId}/`, payload);
  }

  uploadEvidence(responseId: any, payload: any) {
    return this.http.post(
      `grc/audit-responses/${responseId}/evidences/upload/`,
      payload
    );
  }

  downloadEvidence(instanceId: any) {
    return this.http.get(
      `grc/audit-responses/${instanceId}/evidences/download/`,
      {
        responseType: "blob",
      }
    );
  }

  deleteEvidence(responseId: number): Observable<any> {
    return this.http.delete(
      `grc/audit-responses/${responseId}/evidences/delete/`
    );
  }

  getFilteredAuditInstances(qParam: string) {
    return this.http.get(`grc/audit-instances/?${qParam}`);
  }

  getRiskAuditInstances(qParam: string, moduleName: string = "") {
    return this.http.get(
      `grc/audit-instances/risk-audits/?${qParam}` + `&module=${moduleName}`
    );
  }

  getComplianceClauseAuditInstances() {
    return this.http.get(`grc/audit-instances/compliance-audits/`);
  }

  getFilteredIncidents(qParam: string, moduleName: string = "") {
    return this.http.get(`grc/incident/?${qParam}&module=${moduleName}`);
  }

  getAuditInsights() {
    return this.http.get("grc/audit-instances/insights/");
  }

  createAuditResponses(instanceId: any, payload: any) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/responses/`,
      payload
    );
  }

  addAuditResponseEvidence(
    instanceId: number,
    responseId: number,
    body: FormData | object
  ) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/responses/${responseId}/documents/`,
      body,
      { reportProgress: true, observe: "events" }
    );
  }

  updateAuditResponseEvidence(
    instanceId: number,
    responseId: number,
    documentId: number,
    comment: string
  ) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/responses/${responseId}/documents/${documentId}/`,
      { comment }
    );
  }

  uploadAuditResponses(instanceId: any, formData: FormData) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/upload-responses/`,
      formData
    );
  }

  getAuditQuestions(instanceId: any, respondentId: any) {
    return this.http.get(
      `grc/audit-instances/${instanceId}/questions/?respondent_id=${respondentId}`
    );
  }

  markAuditInstanceToProgress(instanceId: any) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/mark-in-progress/`,
      {}
    );
  }

  deleteAuditResponseDocument(responseId: any, documentId: any) {
    return this.http.delete(
      `grc/audit-responses/${responseId}/documents/${documentId}/delete/`
    );
  }

  downloadAuditResponseDocument(responseId: any, documentId: any) {
    return this.http.get(
      `grc/audit-responses/${responseId}/documents/${documentId}/download/`,
      { responseType: "blob" }
    );
  }

  updateAuditTemplate(templateId: number, payload: any): Observable<any> {
    return this.http.put(`grc/audit-templates/${templateId}/`, payload);
  }

  createAuditTemplate(payload: any): Observable<any> {
    return this.http.post(`grc/audit-templates/create-from-json/`, payload);
  }

  getRiskTypes() {
    return this.http.get("valuelists/?type=compliance_risk_class");
  }

  getAuditInstanceDocuments(instanceId: number) {
    return this.http.get<{ documents: any[] }>(
      `grc/audit-instances/${instanceId}/documents/`
    );
  }

  downloadAuditQuestionReferenceDocument(docId: any) {
    return this.http.get(
      `grc/audit-templates/reference-document/${docId}/download/`,
      { responseType: "blob" }
    );
  }

  updateTurnAroundDate(instanceId: any, responseId: any, payload: any) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/responses/${responseId}/turn-around-date/`,
      payload
    );
  }

  finalizeResponses(
    instanceId: any,
    payload: {
      response_ids: number[];
      is_final: boolean;
      visible_to_respondent: boolean;
    }
  ) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/finalize/`,
      payload
    );
  }

  getRiskTypesForAudit() {
    return this.http.get(`valuelists/?type=risk_type`);
  }

  updateAuditStatus(
    instanceId: any,
    payload: {
      status: string;
    }
  ) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/update-status/`,
      payload
    );
  }

  requestDocuments(instanceId: any, payload: any) {
    return this.http.post(
      `grc/audit-instances/${instanceId}/request-documents/`,
      payload
    );
  }

  downloadReport(auditId: number): Observable<Blob> {
    return this.http.get(`grc/audit-instances/${auditId}/report/`, {
      responseType: "blob",
    });
  }

  addResponseComment(responseId: any, payload: any) {
    return this.http.post(
      `grc/audit-responses/${responseId}/comments/`,
      payload
    );
  }

  getThreatProviders() {
    return this.http.get("grc/incident/providers/");
  }

  getIncidentScanFiles() {
    return this.http.get("grc/incident/list-files/");
  }

  scanIncidents(provider: any, file?: string) {
    const payload = file ? { file } : {};
    return this.http.post(`grc/incident/scan/?provider=${provider}`, payload);
  }

  getIncidentScanStatus(providerId: any, type: string = "incident_scan") {
    return this.http.get(
      `grc/incident/status/?provider=${providerId}&type=${type}`
    );
  }
}
